import { Injectable } from '@angular/core';

import { firstValueFrom } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

import { StorageService } from '@repositories/services';

import { ChatHistoryIndex, ChatSummary, StoredChat } from '../types';

/** Contenedor del blob storage donde vive el historial del chat */
const CONTAINER = 'botsessions';

/** Llave del índice, dentro del espacio del usuario */
const INDEX_KEY = 'index';

/** Largo máximo del nombre deducido del primer mensaje */
const TITLE_MAX = 60;

/** Conversaciones que se conservan por usuario, las más viejas se descartan */
const MAX_CHATS = 60;

/**
 * Historial de conversaciones de Bryant, guardado en el blob storage a través
 * del controlador `/api/storage`.
 *
 * El controlador solo expone `get` y `set` por llave, así que el listado no se
 * puede resolver enumerando el contenedor: cada usuario tiene un blob índice
 * (`email/index`) con el resumen de sus conversaciones, y un blob por hilo
 * (`email/uuid`) con los mensajes. Índice e hilo se escriben juntos en cada
 * guardado, por eso las operaciones de escritura tocan dos llaves.
 */
@Injectable({ providedIn: 'root' })
export class ChatHistoryService {

  /**
   * Constructor
   * @param {StorageService} _storage Acceso al blob storage del backend
   */
  constructor(private _storage: StorageService) { }

  /**
   * Genera el identificador de una conversación nueva.
   * @returns {string} Devuelve un uuid
   */
  newId(): string {
    return uuidv4();
  }

  /**
   * Devuelve el índice completo del usuario: el listado y la última
   * conversación abierta, en una sola lectura.
   *
   * @param {string} email Correo del usuario, primera parte de la llave
   * @returns {Promise<ChatHistoryIndex>} Devuelve el índice, vacío si todavía no existe
   */
  async index(email: string): Promise<ChatHistoryIndex> {
    return this.readIndex(email);
  }

  /**
   * Lista las conversaciones del usuario, de la más reciente a la más antigua.
   * @param {string} email Correo del usuario, primera parte de la llave
   * @returns {Promise<ChatSummary[]>} Devuelve el listado, vacío si no hay índice
   */
  async list(email: string): Promise<ChatSummary[]> {
    const index = await this.readIndex(email);

    return index.chats;
  }

  /**
   * Recupera un hilo completo.
   * @param {string} email Correo del usuario
   * @param {string} id Uuid de la conversación
   * @returns {Promise<StoredChat>} Devuelve el hilo, o null si la llave no existe
   */
  async load(email: string, id: string): Promise<StoredChat | null> {
    if (!email || !id) return null;

    const chat = await firstValueFrom(this._storage.get<StoredChat>(CONTAINER, this.key(email, id)));

    return chat && !chat.deleted ? chat : null;
  }

  /**
   * Guarda el hilo y refresca su entrada en el índice.
   * @param {string} email Correo del usuario
   * @param {StoredChat} chat Hilo a guardar
   * @returns {Promise<ChatSummary[]>} Devuelve el índice ya actualizado
   */
  async save(email: string, chat: StoredChat): Promise<ChatSummary[]> {
    if (!email || !chat?.id) return [];

    await firstValueFrom(this._storage.set(CONTAINER, this.key(email, chat.id), chat));

    return this.updateIndex(email, index => {
      const summary: ChatSummary = {
        id: chat.id,
        title: chat.title,
        createdAt: chat.createdAt,
        updatedAt: chat.updatedAt,
        preview: this.previewOf(chat),
        messageCount: chat.messages?.length ?? 0
      };

      index.chats = [summary, ...index.chats.filter(c => c.id !== chat.id)];
      index.lastActiveId = chat.id;
    });
  }

  /**
   * Cambia el nombre visible de una conversación, en el índice y en el hilo.
   * @param {string} email Correo del usuario
   * @param {string} id Uuid de la conversación
   * @param {string} title Nombre nuevo
   * @returns {Promise<ChatSummary[]>} Devuelve el índice ya actualizado
   */
  async rename(email: string, id: string, title: string): Promise<ChatSummary[]> {
    const name = (title || '').trim().slice(0, TITLE_MAX);

    if (!name) return this.list(email);

    const chat = await this.load(email, id);

    if (chat) {
      chat.title = name;
      await firstValueFrom(this._storage.set(CONTAINER, this.key(email, id), chat));
    }

    return this.updateIndex(email, index => {
      index.chats = index.chats.map(c => (c.id === id ? { ...c, title: name } : c));
    });
  }

  /**
   * Da de baja una conversación. El controlador no expone delete, así que la
   * llave se sobrescribe con una lápida y la entrada se saca del índice.
   *
   * @param {string} email Correo del usuario
   * @param {string} id Uuid de la conversación
   * @returns {Promise<ChatSummary[]>} Devuelve el índice ya actualizado
   */
  async remove(email: string, id: string): Promise<ChatSummary[]> {
    if (!email || !id) return [];

    const now = new Date().toISOString();

    const tombstone: StoredChat = {
      id,
      title: '',
      createdAt: now,
      updatedAt: now,
      conversationId: null,
      session: null,
      messages: [],
      deleted: true
    };

    await firstValueFrom(this._storage.set(CONTAINER, this.key(email, id), tombstone));

    return this.updateIndex(email, index => {
      index.chats = index.chats.filter(c => c.id !== id);
      if (index.lastActiveId === id) index.lastActiveId = index.chats[0]?.id ?? null;
    });
  }

  /**
   * Recuerda qué conversación está abierta, para reabrirla al volver al chat.
   * @param {string} email Correo del usuario
   * @param {string} id Uuid de la conversación, o null si se empezó una nueva
   */
  async setLastActive(email: string, id: string | null): Promise<void> {
    await this.updateIndex(email, index => {
      index.lastActiveId = id;
    });
  }

  /**
   * Deduce el nombre de la conversación a partir del primer mensaje del usuario.
   * @param {{ role?: string; content?: string }[]} messages Mensajes del hilo
   * @returns {string} Devuelve el nombre, recortado al largo máximo
   */
  titleFrom(messages: { role?: string; content?: string }[]): string {
    const first = (messages ?? []).find(m => m.role === 'user' && (m.content || '').trim());
    const text = (first?.content ?? '').replace(/\s+/g, ' ').trim();

    if (!text) return '';

    return text.length > TITLE_MAX ? `${text.slice(0, TITLE_MAX - 1).trimEnd()}…` : text;
  }

  // ─── Índice ─────────────────────────────────────────────────────────

  /**
   * Lee el índice del usuario.
   * @param {string} email Correo del usuario
   * @returns {Promise<ChatHistoryIndex>} Devuelve el índice, vacío si no existe
   */
  private async readIndex(email: string): Promise<ChatHistoryIndex> {
    if (!email) return { chats: [], lastActiveId: null };

    const index = await firstValueFrom(
      this._storage.get<ChatHistoryIndex>(CONTAINER, this.key(email, INDEX_KEY))
    );

    return {
      chats: this.sort(index?.chats ?? []),
      lastActiveId: index?.lastActiveId ?? null
    };
  }

  /**
   * Aplica un cambio sobre el índice y lo vuelve a guardar.
   * @param {string} email Correo del usuario
   * @param {(index: ChatHistoryIndex) => void} change Cambio a aplicar
   * @returns {Promise<ChatSummary[]>} Devuelve el listado ya guardado
   */
  private async updateIndex(email: string, change: (index: ChatHistoryIndex) => void): Promise<ChatSummary[]> {
    if (!email) return [];

    const index = await this.readIndex(email);

    change(index);

    // El índice se mantiene acotado: solo entran las conversaciones recientes
    index.chats = this.sort(index.chats).slice(0, MAX_CHATS);

    await firstValueFrom(this._storage.set(CONTAINER, this.key(email, INDEX_KEY), index));

    return index.chats;
  }

  /**
   * Ordena las conversaciones de la más reciente a la más antigua.
   * @param {ChatSummary[]} chats Conversaciones a ordenar
   * @returns {ChatSummary[]} Devuelve una copia ordenada
   */
  private sort(chats: ChatSummary[]): ChatSummary[] {
    return [...chats].sort((a, b) => (b.updatedAt ?? '').localeCompare(a.updatedAt ?? ''));
  }

  /**
   * Arma la llave del storage: el correo del usuario y el uuid del hilo.
   * @param {string} email Correo del usuario
   * @param {string} id Uuid de la conversación, o la llave del índice
   * @returns {string} Devuelve la llave `email/uuid`
   */
  private key(email: string, id: string): string {
    return `${(email || '').trim().toLowerCase()}/${id}`;
  }

  /**
   * Primeras palabras del último mensaje, para el subtítulo de la lista.
   * @param {StoredChat} chat Hilo guardado
   * @returns {string} Devuelve el texto recortado
   */
  private previewOf(chat: StoredChat): string {
    const last = [...(chat.messages ?? [])].reverse().find(m => (m.content || '').trim());
    const text = (last?.content ?? '').replace(/\s+/g, ' ').trim();

    return text.length > 90 ? `${text.slice(0, 89).trimEnd()}…` : text;
  }
}
